import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/lib/store/auth-store";
import { LoginPage } from "@/pages/LoginPage";

// Code-splitting (perf P1): the login screen is the unauthenticated entry point,
// so it stays eager and light. The heavy surfaces — AppPage (pulls maplibre-gl,
// ~790 KB) and the Docs stack (react-markdown/remark) — are lazy chunks fetched
// only when their route is visited, keeping the initial/login JS ~80% smaller.
const AppPage = lazy(() =>
  import("@/pages/AppPage").then((m) => ({ default: m.AppPage })),
);
const DocsLayout = lazy(() =>
  import("@/components/docs/DocsLayout").then((m) => ({ default: m.DocsLayout })),
);
const DocsPage = lazy(() =>
  import("@/pages/DocsPage").then((m) => ({ default: m.DocsPage })),
);

/** Lightweight fallback while a lazy route chunk loads (no heavy imports). */
function RouteFallback() {
  return (
    <div className="grid min-h-dvh place-items-center bg-background text-sm text-muted-foreground">
      Loading…
    </div>
  );
}

/** Wrap a lazy route element in Suspense with the shared fallback. */
function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

/** Gate the main app: unauthenticated visitors are sent to /login. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  return token ? <>{children}</> : <Navigate to="/login" replace />;
}

/** Already signed in? Skip the login screen. */
function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  return token ? <Navigate to="/" replace /> : <>{children}</>;
}

/**
 * Routes:
 *   /login        → visitor Login (light · spacious)
 *   /             → Main app: topbar + map + floating chat (light · spacious)
 *   /docs         → MCP Docs shell (dark); index renders the Overview article
 *   /docs/:slug   → a doc page (connect · tools · configuration · backend-apis …)
 *
 * The docs surface is a nested route: <DocsLayout> owns the dark sidebar +
 * sticky header and renders the active article (<DocsPage>) via <Outlet>, so
 * deep links work and the layout chrome never remounts on nav. Each surface
 * forces its theme via <ThemeScope>; <Toaster> (sonner) is mounted app-wide.
 */
export const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <RedirectIfAuthed>
        <LoginPage />
      </RedirectIfAuthed>
    ),
  },
  {
    path: "/",
    element: (
      <RequireAuth>
        <Lazy>
          <AppPage />
        </Lazy>
      </RequireAuth>
    ),
  },
  {
    path: "/docs",
    element: (
      <Lazy>
        <DocsLayout />
      </Lazy>
    ),
    children: [
      {
        index: true,
        element: (
          <Lazy>
            <DocsPage />
          </Lazy>
        ),
      },
      {
        path: ":slug",
        element: (
          <Lazy>
            <DocsPage />
          </Lazy>
        ),
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);

export { Toaster };
